/**
 * Ask someone to take a chore (docs/01 §3.2).
 *
 * Two things this sheet has to get across, because they are the reasons the
 * feature exists at all:
 *
 *  - **Broadcast is a real choice.** Leaving the target unset asks everyone
 *    eligible and the first to accept takes it. That is the default here: it is
 *    the fastest way to get cover, and picking a name first makes people
 *    hesitate.
 *  - **An accepted swap does not break your streak.** Arranging cover is the
 *    behaviour the system wants to encourage, so the sheet says so before you
 *    commit rather than leaving you to find out.
 *
 * It rises from the bottom edge, so the two buttons that finish it land under
 * the thumb rather than at the top of a phone nobody can reach one-handed.
 */

import { useId, useState } from 'react'
import type { FormEvent } from 'react'
import type { MemberId } from '@office/domain'
import { useDialogFocus } from './dialog.js'
import type { MemberLite } from './live.js'
import './phone.css'

export interface SwapSheetProps {
  /** The chore being handed over, for the heading. */
  choreTitle: string
  dayLabel: string
  /**
   * Everyone the chore may actually be handed to: the definition's rotation
   * pool, minus the person who currently holds it (docs/01 §3.2 — a broadcast
   * goes to "everyone eligible", not to everyone).
   */
  candidates: readonly MemberLite[]
  onSubmit(to: MemberId | null, reason: string | null): void
  onClose(): void
}

const BROADCAST = '__anyone__'

export function SwapSheet({ choreTitle, dayLabel, candidates, onSubmit, onClose }: SwapSheetProps) {
  const [target, setTarget] = useState<string>(BROADCAST)
  const [reason, setReason] = useState('')
  const titleId = useId()
  const groupId = useId()
  const reasonId = useId()
  const { containerRef, initialRef, onKeyDown } = useDialogFocus<
    HTMLFormElement,
    HTMLInputElement
  >(onClose)

  const submit = (e: FormEvent) => {
    e.preventDefault()
    const trimmed = reason.trim()
    onSubmit(target === BROADCAST ? null : (target as MemberId), trimmed === '' ? null : trimmed)
  }

  return (
    <div className="ph-sheet-backdrop">
      <form
        ref={containerRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="card stack gap-3 ph-sheet"
        onSubmit={submit}
        onKeyDown={onKeyDown}
      >
        <div>
          <h2 id={titleId} className="ph-sheet-title">
            Ask someone to take it
          </h2>
          <p className="ph-sheet-sub">
            {choreTitle} · {dayLabel}
          </p>
        </div>

        <fieldset className="ph-fieldset" aria-describedby={groupId}>
          <legend className="ph-legend">Who should take it?</legend>
          <label className="ph-option">
            <input
              ref={initialRef}
              type="radio"
              name="swap-target"
              value={BROADCAST}
              checked={target === BROADCAST}
              onChange={() => setTarget(BROADCAST)}
            />
            <span>
              Anyone
              <span className="ph-option-hint">
                Asks everyone eligible. First to accept takes it.
              </span>
            </span>
          </label>
          {candidates.map(c => (
            <label key={String(c.id)} className="ph-option">
              <input
                type="radio"
                name="swap-target"
                value={String(c.id)}
                checked={target === String(c.id)}
                onChange={() => setTarget(String(c.id))}
              />
              <span>{c.name}</span>
            </label>
          ))}
          <p id={groupId} className="ph-hint">
            {candidates.length === 0
              ? 'Nobody else is in the rotation for this chore yet — a broadcast will wait for one.'
              : 'Nothing moves until someone accepts.'}
          </p>
        </fieldset>

        <div className="stack gap-1">
          <label htmlFor={reasonId} className="ph-field-label">
            Why? (optional)
          </label>
          <textarea
            id={reasonId}
            className="ph-textarea"
            rows={2}
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="Out all evening"
          />
        </div>

        <p className="ph-chip ph-chip-block" data-tone="good">
          <span aria-hidden="true">🔥 </span>
          Your streak is safe — an accepted swap does not break it
        </p>

        <div className="ph-sheet-actions">
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary">
            Send request
          </button>
        </div>
      </form>
    </div>
  )
}
